// ⚡ "Connect your AI" — consent page for Sign in with Heroshhi (OAuth). Route: /connect
import React, { useEffect, useState } from 'react';
import config from '../config';

const KEYS = ['client_id', 'redirect_uri', 'code_challenge', 'state', 'scope', 'resource'];

export default function ConnectAIPage() {
  const params = new URLSearchParams(window.location.search);
  const req = Object.fromEntries(KEYS.map(k => [k, params.get(k) || '']));
  const [app, setApp] = useState(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const token = localStorage.getItem('token');
  let user = null; try { user = JSON.parse(localStorage.getItem('user') || 'null'); } catch (e) { user = null; }

  const toLogin = () => {
    sessionStorage.setItem('hs_connect_return', window.location.pathname + window.location.search);
    window.location.replace('/login');
  };
  const switchAccount = () => { localStorage.removeItem('token'); localStorage.removeItem('user'); toLogin(); };

  useEffect(() => {
    if (!token) { toLogin(); return; }
    if (!req.client_id || !req.redirect_uri || !req.code_challenge) { setError('This connection link is incomplete. Please start again from your AI assistant.'); return; }
    fetch(config.API_URL + '/api/oauth/client?' + new URLSearchParams({ client_id: req.client_id, redirect_uri: req.redirect_uri }))
      .then(r => r.json())
      .then(d => (d.success ? setApp(d) : setError('This app is not registered with Heroshhi. Please start again from your AI assistant.')))
      .catch(() => setError('Could not reach Heroshhi. Check your connection and try again.'));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const decide = async (allow) => {
    setBusy(true);
    try {
      const r = await fetch(config.API_URL + '/api/oauth/approve', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: 'Bearer ' + token },
        body: JSON.stringify({ ...req, allow }),
      });
      if (r.status === 401) { localStorage.removeItem('token'); toLogin(); return; }
      const d = await r.json();
      if (d.success && d.redirect) { window.location.replace(d.redirect); return; }
      setError(d.message || 'Something went wrong. Please start again from your AI assistant.');
    } catch (e) { setError('Could not reach Heroshhi. Check your connection and try again.'); }
    setBusy(false);
  };

  if (!token) return null;
  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4 py-10">
      <div className="w-full max-w-md bg-white rounded-2xl shadow-sm border border-gray-200 p-7">
        <img src="/heroshhi-logo.png" alt="Heroshhi" className="h-10 mb-6" />
        {error ? (
          <>
            <h1 className="text-lg font-semibold text-gray-900 mb-2">Can't connect</h1>
            <p className="text-sm text-gray-600">{error}</p>
          </>
        ) : !app ? (
          <p className="text-sm text-gray-500">Checking the request…</p>
        ) : (
          <>
            <h1 className="text-xl font-semibold text-gray-900 leading-snug">Connect {app.name} to Heroshhi</h1>
            <p className="text-sm text-gray-500 mt-1">{app.host} is asking to use your Heroshhi account.</p>
            <div className="mt-5 rounded-xl bg-gray-50 border border-gray-200 p-4 text-sm text-gray-700">
              <p className="font-medium text-gray-900 mb-2">{app.name} will be able to:</p>
              <p className="mb-1.5">• See the leads you can see in Heroshhi — who needs attention, follow-ups, notes, messages and campaign results.</p>
              <p>• Log calls, add notes and schedule follow-ups for you. It asks you before each change.</p>
            </div>
            <p className="text-xs text-gray-500 mt-4">
              Signed in as <span className="font-medium text-gray-700">{(user && (user.email || user.name)) || 'your Heroshhi account'}</span>
              {' · '}<button type="button" onClick={switchAccount} className="underline hover:text-gray-700">Not you?</button>
            </p>
            <p className="text-xs text-gray-500 mt-1">You can disconnect it at any time.</p>
            <div className="flex gap-3 mt-6">
              <button type="button" disabled={busy} onClick={() => decide(false)}
                className="flex-1 py-2.5 rounded-lg border border-gray-300 text-gray-700 text-sm font-medium hover:bg-gray-50 disabled:opacity-50">Cancel</button>
              <button type="button" disabled={busy} onClick={() => decide(true)}
                className="flex-1 py-2.5 rounded-lg bg-blue-600 text-white text-sm font-medium hover:bg-blue-700 disabled:opacity-50">{busy ? 'Connecting…' : 'Allow'}</button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
